import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import { $loggedInUser } from "./token.atom";

const logger = new Logger("login-context");

/**
 * Guard atom that ensures the SQLite cache belongs to the currently logged-in user.
 *
 * Both `$cacheDocuments` and `$cacheWorkspaces` depend on this atom so that
 * stale data from a previous account is cleared before it reaches the UI.
 *
 * When `$loggedInUser` changes (e.g. after a login/logout cycle with a different
 * account), this atom compares the new user ID against the one stored in the
 * `login_context` table. On mismatch it deletes all cached documents and
 * workspaces, then records the new user ID.
 */
export const $loginContextGuard = atom(async (get) => {
	const loggedInUser = get($loggedInUser);
	const userId = loggedInUser?.sub;

	if (!userId) {
		return;
	}

	const cache = getCache();
	const storedUserId = await cache.loginContext.getUserId();

	if (storedUserId !== userId) {
		logger.info("User context changed, clearing stale cache", { storedUserId, userId });
		try {
			await cache.loginContext.clearUserData();
			await cache.loginContext.setUserId(userId);
		} catch (error) {
			// Don't set userId so the guard retries on next evaluation.
			// Cache loading proceeds with potentially stale data; remote fetches will replace it.
			logger.warn("Failed to clear stale cache, will retry on next evaluation", { error });
		}
	}
});
$loginContextGuard.debugLabel = "loginContextGuard";
